/**
 * EcommerceManager — Thin orchestrator for e-commerce UI.
 *
 * Owns cart state (item count, total, currency) and delegates all UI
 * operations to the active presenter (DesktopPresenter or MobilePresenter).
 * The presenter is resolved lazily on first use since mobile detection
 * requires the DOM to be ready.
 *
 * Used by TTPEcommerceWidget (composition pattern).
 * Does NOT depend on VoiceInterface or VoiceSDK directly.
 */
import { DesktopPresenter } from './DesktopPresenter.js';
import { MobilePresenter } from './MobilePresenter.js';

export class EcommerceManager {
  constructor(config = {}) {
    this.shadowRoot = config.shadowRoot;
    this.primaryColor = config.primaryColor || '#3b82f6';
    this.onProductSelected = config.onProductSelected || (() => {});
    this.onProductDeselected = config.onProductDeselected || (() => {});

    this.cartItemCount = 0;
    this.cartTotal = 0;
    this.currencySymbol = '$';

    this._presenter = null;
  }

  // ═══════════════════════════════════════════
  // PRESENTER RESOLUTION
  // ═══════════════════════════════════════════

  /**
   * Lazily create the correct presenter based on device type.
   * Cached after first call.
   */
  get presenter() {
    if (!this._presenter) {
      this._presenter = this._createPresenter();
    }
    return this._presenter;
  }

  _createPresenter() {
    if (this._isMobileCall()) {
      return new MobilePresenter({
        primaryColor: this.primaryColor,
        onProductAdd: (product) => this._handleProductAdd(product),
      });
    }

    return new DesktopPresenter({
      shadowRoot: this.shadowRoot,
      primaryColor: this.primaryColor,
      onProductAdd: (product) => this._handleProductAdd(product),
      onProductUndo: (product) => this.onProductDeselected(product),
    });
  }

  _isMobileCall() {
    return !!document.getElementById('mobile-voice-call-bar-container');
  }

  // ═══════════════════════════════════════════
  // CURRENCY
  // ═══════════════════════════════════════════

  _resolveCurrency(currencyCode) {
    return currencyCode === 'ILS' ? '₪' : '$';
  }

  // ═══════════════════════════════════════════
  // CART STATE
  // ═══════════════════════════════════════════

  _buildCartInfo() {
    return {
      itemCount: this.cartItemCount,
      total: this.cartTotal,
      currency: this.currencySymbol,
    };
  }

  // ═══════════════════════════════════════════
  // MESSAGE HANDLERS (called by TTPEcommerceWidget)
  // ═══════════════════════════════════════════

  /**
   * Alias for handleShowProducts — unified contract method.
   */
  handleShowItems(message) {
    return this.handleShowProducts(message);
  }

  handleShowProducts(message) {
    const { products, title, layout } = message;

    if (!products || products.length === 0) {
      console.warn('[EcommerceManager] show_products with no products');
      return;
    }

    this.currencySymbol = this._resolveCurrency(products[0].currency);
    this.presenter.showProducts(products, title, layout);
  }

  handleCartUpdated(message) {
    const { product, action, cartTotal, cartItemCount } = message;

    if (cartTotal !== undefined && cartItemCount !== undefined) {
      const currency = this._resolveCurrency(message.currency || (this.currencySymbol === '₪' ? 'ILS' : 'USD'));
      this.cartItemCount = cartItemCount;
      this.cartTotal = cartTotal;
      this.currencySymbol = currency;

      this.presenter.updateCart(this._buildCartInfo());
    }

    if (action === 'added' && product) {
      this.presenter.onProductAdded(product, this._buildCartInfo());
      /* Products are hidden in onProductAdded; panel shrinks to original when toast is gone */
    }
  }

  /**
   * Directly handle a message by type (convenience for testing).
   */
  handleMessage(payload) {
    const type = payload.t || payload.type;
    if (type === 'show_products') {
      this.handleShowProducts(payload);
    } else if (type === 'cart_updated') {
      this.handleCartUpdated(payload);
    }
  }

  // ═══════════════════════════════════════════
  // INTERNAL: product add callback from presenter
  // ═══════════════════════════════════════════

  _handleProductAdd(product) {
    const price = product.price || 0;
    const qty = product.selectedAmount || 1;
    const currency = this._resolveCurrency(product.currency);

    this.cartItemCount += 1;
    this.cartTotal += price * qty;
    this.currencySymbol = currency;

    this.presenter.onProductAdded(product, this._buildCartInfo());
    this.onProductSelected(product);
  }

  // ═══════════════════════════════════════════
  // PUBLIC API
  // ═══════════════════════════════════════════

  /**
   * Hide any displayed products and reset the UI to summary mode.
   * Called by TTPEcommerceWidget on call end.
   */
  hideProducts() {
    this.presenter.hideProducts();
  }

  // ═══════════════════════════════════════════
  // CLEANUP
  // ═══════════════════════════════════════════

  destroy() {
    if (this._presenter) {
      this._presenter.destroy();
      this._presenter = null;
    }
  }
}
